import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { chatAPI, documentsAPI } from '../utils/api';
import './ChatInterface.css';

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentError, setDocumentError] = useState(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch user's documents on mount
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setIsLoadingDocuments(true);
        const response = await documentsAPI.getAll();
        const docs = response.documents || [];
        setDocuments(docs);
        
        // Auto-select the first 'ready' document if available
        const readyDocument = docs.find(doc => doc.status === 'ready');
        
        if (readyDocument) {
          setDocumentId(readyDocument.id);
          setDocumentError(null);
        } else if (docs.length > 0) {
          // Document exists but not ready
          const processingDoc = docs.find(doc => doc.status === 'processing');
          if (processingDoc) {
            setDocumentError('Your document is still being processed. Please wait...');
          } else {
            setDocumentError('No ready document found. Please upload a PDF document.');
          }
        } else {
          setDocumentError('No document uploaded. Please upload a PDF document first.');
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setDocumentError('Unable to load documents. Please try again.');
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, []);

  // Handle document selection
  const handleDocumentSelect = (selectedDocId) => {
    if (selectedDocId === documentId) return; // Already selected
    
    const selectedDoc = documents.find(doc => doc.id === selectedDocId);
    
    if (selectedDoc && selectedDoc.status === 'ready') {
      setDocumentId(selectedDocId);
      setDocumentError(null);
      // Clear messages and session when switching documents
      setMessages([]);
      setSessionId(null);
    } else if (selectedDoc && selectedDoc.status === 'processing') {
      setDocumentError('This document is still being processed. Please wait...');
    } else {
      setDocumentError('This document is not ready. Please select a ready document.');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    // Check if document is selected
    if (!documentId) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Please select a document from the dropdown above before sending a message.'
      }]);
      return;
    }

    // Verify selected document is ready
    const selectedDoc = documents.find(doc => doc.id === documentId);
    if (!selectedDoc || selectedDoc.status !== 'ready') {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ The selected document is not ready. Please select a ready document.'
      }]);
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Send message with document_id
      const response = await chatAPI.sendMessage(userMessage, sessionId, documentId);
      
      if (response) {
        if (!sessionId && response.session_id) {
          setSessionId(response.session_id);
        }
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.response || response.message || 'Sorry, I could not generate a response.'
        }]);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Sorry, I encountered an error. Please try again.';
      console.error('Error sending message:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        documentId: documentId,
        message: errorMessage
      });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Error: ${errorMessage}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-interface">
      {/* Document Selector */}
      <div className="document-selector">
        <label htmlFor="document-select" className="document-selector-label">
          📄 Select Document:
        </label>
        {isLoadingDocuments ? (
          <div className="document-loading">Loading documents...</div>
        ) : documents.length > 0 ? (
          <select
            id="document-select"
            className="document-select"
            value={documentId || ''}
            onChange={(e) => handleDocumentSelect(Number(e.target.value))}
          >
            <option value="">-- Select a document --</option>
            {documents.map((doc) => (
              <option key={doc.id} value={doc.id} disabled={doc.status !== 'ready'}>
                {doc.filename} {doc.status === 'ready' ? '✓' : doc.status === 'processing' ? '(Processing...)' : '(Not Ready)'}
              </option>
            ))}
          </select>
        ) : (
          <div className="document-empty">No documents available. Please upload a PDF first.</div>
        )}
        {documentError && (
          <div className="document-error-message">⚠️ {documentError}</div>
        )}
      </div>

      <div className="chat-container">
        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h3>👋 Welcome to your PDF Chatbot!</h3>
              {documentError ? (
                <p className="error-text">⚠️ {documentError}</p>
              ) : documentId ? (
                <p>Ask me anything about the selected document.</p>
              ) : (
                <p>Please select a document from the dropdown above to start chatting.</p>
              )}
            </div>
          )}
          
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              <div className="message-content">
                {msg.role === 'assistant' ? (
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <form className="chat-input-form" onSubmit={sendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask a question about your PDF..."
            className="chat-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!inputMessage.trim() || isLoading}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
